<template>
    <service-card
        :service-tips="serviceTips"
        :service="service"
        icon="mdi-car"
        :is-first="isFirst"
        :order="order"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service="service"
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :service-image="mainImage"
                :service-label="serviceDetails.transport"
                :is-package="isPackage"
                :tariff-name="service.serviceDetails[0].tariffName"
                :order="order"
                :order-end-date="orderEndDate"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
            >
                <div class="order-info-base-info">
                    <div class="order-info-base-info__icon align-center">
                        <v-icon start size="x-small" style="height: max-content">
                            {{ departureIcon }}
                        </v-icon>
                        <span class="text-body-2">{{ $t('transfers.from') }}</span>
                    </div>
                    <div class="order-info-base-info__text">
                        <v-skeleton-loader v-if="isInfoLoading" type="text" class="full-width" />
                        <template v-else>
                            <span v-html="departurePoint.destinationPointName" />
                            <span
                                v-if="mapPointDeparture"
                                class="text-body-2 text-link text-lowercase ml-2"
                                @click="mapDepartureModal = true"
                            >
                                {{ $t('show_on_map') }}
                            </span>
                        </template>
                        <client-only>
                            <ModalWrapper
                                v-if="mapPointDeparture && mapPointDeparture.latitude && mapPointDeparture.longitude"
                                v-model="mapDepartureModal"
                                :max-width="500"
                            >
                                <map-location
                                    v-if="mapDepartureModal"
                                    :latitude="mapPointDeparture.latitude"
                                    :longitude="mapPointDeparture.longitude"
                                    :height="`500px`"
                                />
                            </ModalWrapper>
                        </client-only>
                    </div>
                </div>
                <div v-if="departurePointName" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small" style="height: max-content">
                            {{ departureIcon }}
                        </v-icon>
                        <span class="text-body-2"> {{ $t('departure_trip') }}</span>
                    </span>
                    <span class="order-info-base-info__text" v-html="departurePointName" />
                </div>
                <div class="order-info-base-info">
                    <div class="order-info-base-info__icon align-center">
                        <v-icon start size="x-small" style="height: max-content">
                            {{ arrivalIcon }}
                        </v-icon>
                        <span class="text-body-2">{{ $t('transfers.to') }}</span>
                    </div>
                    <div class="order-info-base-info__text">
                        <v-skeleton-loader v-if="isInfoLoading" type="text" class="full-width" />
                        <template v-else>
                            <span v-html="arrivalPoint.destinationPointName" />
                            <span
                                v-if="mapPointArrival"
                                class="text-body-2 text-link text-lowercase ml-2"
                                @click="mapArrivalModal = true"
                            >
                                {{ $t('show_on_map') }}
                            </span>
                        </template>
                        <client-only>
                            <ModalWrapper
                                v-if="mapPointArrival && mapPointArrival.latitude && mapPointArrival.longitude"
                                v-model="mapArrivalModal"
                                :max-width="500"
                            >
                                <map-location
                                    v-if="mapArrivalModal"
                                    :latitude="mapPointArrival.latitude"
                                    :longitude="mapPointArrival.longitude"
                                    :height="`500px`"
                                />
                            </ModalWrapper>
                        </client-only>
                    </div>
                </div>
                <div v-if="arrivalPointName" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small" style="height: max-content">
                            {{ arrivalIcon }}
                        </v-icon>
                        <span class="text-body-2"> {{ $t('arrival_trip') }}</span>
                    </span>
                    <span class="order-info-base-info__text" v-html="arrivalPointName" />
                </div>
                <div v-if="pickUpSign" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-card-bulleted-outline</v-icon>
                        <span class="text-body-2">{{ $t('transfers.pickup_sign') }}:</span>
                    </span>
                    <span class="order-info-base-info__text"> {{ pickUpSign }}</span>
                </div>
                <template v-slot:extraTime>
                    <div v-if="info.duration || isInfoLoading" class="order-info-base-info">
                        <span class="order-info-base-info__icon">
                            <v-icon start size="x-small">mdi-timer-outline</v-icon>
                            <span class="text-body-2">{{ $t('duration') }}</span>
                        </span>
                        <div class="order-info-base-info__text">
                            <v-skeleton-loader v-if="isInfoLoading" type="text" class="full-width" />
                            <span v-else>
                                {{ transferDuration }}
                            </span>
                        </div>
                    </div>
                </template>
                <transfer-driver-languages :is-show-label="true" :lang-codes="langCodes" />
                <transfer-operated-by-label
                    :product="{
                        info: {operatedBy: service.serviceDetails[0].operatedBy, supplierCode: service.supplierCode},
                    }"
                />
                <div v-if="service.status === 'Quote' && !isReadOnly" class="d-flex align-center">
                    <v-icon start size="small" color="primary" @click="fillTransferBookingDataModal = true">
                        mdi-pencil
                    </v-icon>
                    <span class="text-caption cursor-pointer text-primary" @click="fillTransferBookingDataModal = true">
                        {{ $t('enter_details') }}
                    </span>
                    <fill-transfer-booking-data-modal
                        v-if="isMinimized === false || infoLoadingFinished"
                        v-model="fillTransferBookingDataModal"
                        :service="service"
                        :order="order"
                        :transfer-info="transferInfo"
                        :departure-point-prop="departurePoint"
                        :arrival-point-prop="arrivalPoint"
                        @save-booking-data="getDestinationPoint"
                    />
                </div>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <service-info-dialog
                ref="serviceInfoDialog"
                v-model="modal"
                :service="service"
                :service-type="
                    service.serviceDetails[0].transferType && service.serviceDetails[0].transferType !== 'UNKNOWN'
                        ? $t(`filters_transferType.${service.serviceDetails[0].transferType}`)
                        : null
                "
                enable-custom-product-info
                :info="info"
                :image="mainImage"
                :is-info-loading="isInfoLoading"
                @close-modal="closeModal"
            >
                <template v-if="transferInfo" v-slot:serviceInfo>
                    <div v-if="serviceDetails.languages && serviceDetails.languages.length">
                        <strong>{{ $t('language') }}: {{ serviceDetails.languages.join(', ') }}</strong>
                    </div>
                    <div v-if="info.shortDescription" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('description_type.short') }}
                        </span>
                        <span v-html="info.shortDescription" />
                    </div>
                    <div v-if="info.fullDescription" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('description_type.general') }}
                        </span>
                        <span v-html="info.fullDescription" />
                    </div>
                    <div v-if="info.pleaseNote" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('important_information') }}
                        </span>
                        <span v-html="info.pleaseNote" />
                    </div>
                    <template v-if="info && info.descriptions && info.descriptions.length">
                        <div class="mt-2" v-html="htmlDescriptions" />
                    </template>
                    <div v-if="transferInfo.comments && transferInfo.comments.length">
                        <div v-for="(comm, index) in transferInfo.comments" :key="index">
                            {{ comm }}
                        </div>
                    </div>
                </template>
            </service-info-dialog>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Prop, Inject} from 'vue-facing-decorator'
    import ServiceInfoDialog from '~/components/account/orderServices/ServiceInfoDialog.vue'
    import ServiceCard from '~src/components/account/orderServices/serviceCard.src'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'
    import TransferDriverLanguages from '@/components/snippets/TransferDriverLanguages.vue'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import FillTransferBookingDataModal from '@/components/account/modals/newOrderRequest/FillTransferBookingDataModal.vue'
    import MapLocation from '@/components/info/MapLocation.vue'

    import TransferOperatedByLabel from '@/components/transfers/snippets/TransferOperatedByLabel.vue'

    @Component({
        components: {
            TransferOperatedByLabel,
            MapLocation,
            FillTransferBookingDataModal,
            TransferDriverLanguages,
            ServiceCard,
            OrderInfoBase,
            ServiceInfoDialog,
        },
    })
    export class TransferService extends ServiceBase {
        @Prop({required: true}) service
        @Prop({default: false}) isPackage
        @Prop() isFirst
        @Inject({default: false}) isReadOnly

        transferInfo = null
        modal = false
        departurePoint = {destinationPointName: null}
        departurePointName = null
        arrivalPointName = null
        arrivalPoint = {destinationPointName: null}
        fillTransferBookingDataModal = false
        mapDepartureModal = false
        mapArrivalModal = false

        async loadProductInfo() {
            this.isInfoLoading = true

            await this.getDestinationPoint()

            this.isInfoLoading = false
            this.infoLoadingFinished = true
        }

        closeModal() {
            this.modal = false
        }

        // TODO Need refactoring this shit
        async getDestinationPoint() {
            const destinationPoint = async destination => {
                const place = this.serviceDetails[`${destination}Place`],
                    id = this.serviceDetails[`${destination}CityId`]

                if (place.otherAirportCode || place.flightNumber) {
                    const [airport] = place.otherAirportCode
                        ? await this.$api.airports.get({iataCode: place.otherAirportCode, limit: 1})
                        : []

                    const flightNumber = place.flightNumber
                        ? `${this.$t('flight_number')}: ${place.flightNumber}`
                        : null
                    const airportText = airport
                        ? `${destination === 'departure' ? 'from' : 'to'} ${airport.name}`
                        : null
                    const terminal = place.terminal ? `${this.$t('terminal')}: ${place.terminal}` : null
                    const dateTimeFormatted = place.dateTime ? this.$filters.dateTimeFormat(place.dateTime) : null
                    const tripArray = []

                    if (flightNumber) {
                        tripArray.push(flightNumber)
                    }
                    if (airportText) {
                        tripArray.push(airportText)
                    }
                    if (airport?.cityName) {
                        tripArray.push(airport.cityName)
                    }
                    if (terminal) {
                        tripArray.push(terminal)
                    }
                    if (dateTimeFormatted) {
                        tripArray.push(dateTimeFormatted)
                    }

                    if (destination === 'departure') {
                        this.departurePointName = tripArray.join(', ')
                    }
                    if (destination === 'arrival') {
                        this.arrivalPointName = tripArray.join(', ')
                    }
                }

                if (place.airportCode) {
                    const [airport] = await this.$api.airports.get({
                        iataCode: place.airportCode,
                        limit: 1,
                    })
                    const destinationPointName =
                        `${airport.name ? airport.name + ', ' : ''} ${airport.cityName}` +
                        (airport.type === 'airport' ? ` (${airport.iataCode})` : '')

                    return {...airport, destinationPointName}
                }

                const {
                    cities: [city],
                } = await this.$api.locations.get({
                    id,
                    limitCities: 1,
                })

                if (place.hotelName) {
                    const destinationPointName = `<span>${
                        place.hotelName ? place.hotelName : ''
                    }</span>${place.hotelName ? ', ' : ''} ${city.name}, ${city.parentName}${
                        place.address ? `, ${place.address}` : ''
                    }${place.postalCode ? `, ${place.postalCode}` : ''}`

                    return {...place, destinationPointName}
                }

                if (place.trainNumber) {
                    let pointName = city.name

                    if (destination === 'departure') {
                        const departureCityId = this.serviceDetails[`${destination}Place`].cityId
                        const {
                            cities: [departureCity],
                        } = departureCityId
                            ? await this.$api.locations.get({id: departureCityId, limitCities: 1})
                            : {cities: []}
                        const trainNumberText = `${this.$t('transfers.train_number')}: ${place.trainNumber}`
                        const cityText = departureCity ? `${this.$t('from')} ${departureCity.name || ''}` : null
                        const dateTimeFormatted = this.$filters.dateTimeFormat(place.dateTime)
                        const departurePointNameArray = [trainNumberText]

                        if (cityText) {
                            departurePointNameArray.push(cityText)
                        }
                        if (dateTimeFormatted) {
                            departurePointNameArray.push(dateTimeFormatted)
                        }

                        this.departurePointName = departurePointNameArray.join(', ')
                    }

                    if (destination === 'arrival') {
                        const arrivalCityId = this.serviceDetails[`${destination}Place`].cityId
                        const {
                            cities: [arrivalCity],
                        } = arrivalCityId
                            ? await this.$api.locations.get({id: arrivalCityId, limitCities: 1})
                            : {cities: []}
                        const trainNumberText = `${this.$t('transfers.train_number')}: ${place.trainNumber}`
                        const cityText = arrivalCityId ? `${this.$t('to')} ${arrivalCity.name || ''}` : null
                        const dateTimeFormatted = this.$filters.dateTimeFormat(place.dateTime)
                        const arrivalPointNameArray = [trainNumberText]

                        if (cityText) {
                            arrivalPointNameArray.push(cityText)
                        }
                        if (dateTimeFormatted) {
                            arrivalPointNameArray.push(dateTimeFormatted)
                        }

                        this.arrivalPointName = arrivalPointNameArray.join(', ')
                    }

                    if (place.iataCode) {
                        const {
                            trainStations: [trainStation],
                        } = await this.$api.trainStations.get({
                            iataCode: place.iataCode,
                            limit: 1,
                            productType: 'TRANSFER',
                        })
                        const destinationPointName = `${pointName ? pointName + ', ' : ''} ${this.$t(
                            'transfers.train_station'
                        )}: ${trainStation.name})`

                        return {...trainStation, destinationPointName}
                    }

                    if (place.trainStationCode) {
                        const locationId = this.serviceDetails[`${destination}CityId`]
                        const productType = 'TRANSFER'
                        const trainStation = (await this.$api.trainStations.get({locationId, productType})).find(
                            station => station.code === place.trainStationCode
                        )

                        pointName = `${city.name}, ${trainStation.name}`
                    }

                    return {...place, destinationPointName: pointName}
                }

                if (place.shipNumber || place.portName) {
                    let pointName = city.name

                    if (place.portName) {
                        pointName = `${this.$t('port')}: ${place.portName}, ${city.name}`
                    }
                    if (destination === 'departure' && place.shipNumber) {
                        const departureCityId = this.serviceDetails[`${destination}Place`].cityId
                        let cityText
                        if (departureCityId) {
                            const {
                                cities: [departureCity],
                            } = await this.$api.locations.get({id: departureCityId, limitCities: 1})
                            cityText = `${this.$t('from')} ${departureCity.name}`
                        }

                        const shipNumberText = `${this.$t('cruises.ship_number')}: ${place.shipNumber}`
                        const dateTimeFormatted = this.$filters.dateTimeFormat(place.dateTime)

                        this.departurePointName = `${shipNumberText}${
                            cityText ? `, ${cityText}` : ''
                        }, ${dateTimeFormatted}`
                    }
                    if (destination === 'arrival' && place.shipNumber) {
                        const arrivalCityId = this.serviceDetails[`${destination}Place`].cityId
                        let cityText
                        if (arrivalCityId) {
                            const {
                                cities: [arrivalCity],
                            } = await this.$api.locations.get({id: arrivalCityId, limitCities: 1})
                            cityText = `${this.$t('to')} ${arrivalCity.name}`
                        }

                        const shipNumberText = `${this.$t('cruises.ship_number')}: ${place.shipNumber}`
                        const dateTimeFormatted = this.$filters.dateTimeFormat(place.dateTime)

                        this.arrivalPointName = `${shipNumberText}${
                            cityText ? `, ${cityText}` : ''
                        }, ${dateTimeFormatted}`
                    }

                    return {...place, destinationPointName: pointName}
                }

                if (place.trainStationCode) {
                    const locationId = this.serviceDetails[`${destination}CityId`]
                    const productType = 'TRANSFER'
                    const trainStation = (await this.$api.trainStations.get({locationId, productType})).find(
                        station => station.code === place.trainStationCode
                    )
                    const pointName = `${trainStation.name}`

                    return {...place, destinationPointName: `${pointName} ${city.name}`}
                }

                if (place.name) {
                    return {
                        ...place,
                        destinationPointName:
                            place.name + `${!place.latitude && !place.longitude ? ', ' + city.name : ''}`,
                    }
                }

                return {
                    ...place,
                    destinationPointName: `${place.address ? place.address + ',' : ''} ${
                        place.postalCode ? place.postalCode + ',' : ''
                    } ${city.name}, ${city.parentName}`,
                }
            }

            try {
                this.transferInfo = await this.$api.transferInfo.get({
                    processId: this.service.processId.replace(/^.{6}/, ''),
                })
                const [arrivalPoint, departurePoint] = await Promise.all([
                    destinationPoint('arrival'),
                    destinationPoint('departure'),
                ])
                this.arrivalPoint = arrivalPoint
                this.departurePoint = departurePoint
            } catch (e) {
                console.error(e)
            }
        }

        getIcon(place) {
            switch (this.getPointType(place)) {
                case 'airport':
                    return 'mdi-airport'
                case 'hotel':
                    return 'mdi-hotel'
                case 'train':
                    return 'mdi-train'
                case 'port':
                    return 'mdi-ferry'
                case 'manual':
                    return 'mdi-map-marker-outline'
                default:
                    return 'mdi-city'
            }
        }

        getPointType(place) {
            if (this.serviceDetails[place].airportCode) {
                return 'airport'
            } else if (this.serviceDetails[place].hotelCode) {
                return 'hotel'
            } else if (this.serviceDetails[place].trainNumber || this.serviceDetails[place].trainStationCode) {
                return 'train'
            } else if (this.serviceDetails[place].shipNumber || this.serviceDetails[place].portName) {
                return 'port'
            }
            const pointType = place === 'arrivalPlace' ? 'arrival' : 'departure'
            if (
                this.transferInfo?.transferPoints?.find(
                    point => point.pointType === pointType && point.type === 'MANUAL'
                )
            ) {
                return 'manual'
            }
            return 'address'
        }

        get langCodes() {
            return this.serviceDetails.languages
        }

        get serviceCheckIn() {
            const pickUpTime = this.serviceDetails.pickupTime
            const date = this.$filters.dateFormat(this.service.startDateTime)
            if (pickUpTime) {
                return `<span>${date}</span> <small>${pickUpTime}</small>`
            }
            return `<span>${date}</span>`
        }

        get serviceCheckOut() {
            const date = this.$filters.dateFormat(this.service.endDateTime)
            return `<span>${date}</span>`
        }

        get serviceDetails() {
            return this.service.serviceDetails[0]
        }

        get departureIcon() {
            return this.getIcon('departurePlace')
        }

        get arrivalIcon() {
            return this.getIcon('arrivalPlace')
        }

        get mapPointDeparture() {
            return this.serviceDetails.departurePlace?.latitude && this.serviceDetails.departurePlace?.longitude
                ? this.serviceDetails.departurePlace
                : null
        }

        get mapPointArrival() {
            return this.serviceDetails.arrivalPlace?.latitude && this.serviceDetails.arrivalPlace?.longitude
                ? this.serviceDetails.arrivalPlace
                : null
        }

        get pickUpSign() {
            return this.service.travelers[0].pickUpSign
        }

        get mainImage() {
            if (this.transferInfo && this.transferInfo.images && this.transferInfo.images.length) {
                let image = this.transferInfo.images.find(el => el.mainImage)
                if (!image) {
                    image = this.transferInfo.images[0]
                }
                return image.url
            }
            return null
        }

        get info() {
            return {...this.transferInfo, ...{mainImage: this.mainImage}}
        }

        get transferDuration() {
            const hours = Math.floor(this.info.duration / 60)
            const minutes = this.info.duration % 60
            if (minutes) return `${hours} ` + this.$t('hour', hours) + ` ${minutes} ` + this.$t('minute', minutes)

            return `${hours} ` + this.$t('hour', hours)
        }

        get isTO1() {
            return authStore.isTO1
        }

        get htmlDescriptions() {
            if (!this.info?.descriptions?.length) return

            const firstDescription = this.info.descriptions[0]
            if (typeof firstDescription === 'string' || firstDescription instanceof String) {
                return this.info.descriptions.map(item => `<div>${item}</div>`).join('')
            }

            const descriptionsGroupedByTitle = new Map()
            this.info.descriptions.forEach(({title, description}) => {
                const listByTitle = descriptionsGroupedByTitle.get(title)

                if (listByTitle) {
                    listByTitle.push(description)
                } else {
                    descriptionsGroupedByTitle.set(title, [description])
                }
            })

            let fullHTML = ''
            descriptionsGroupedByTitle.forEach((descriptions, title) => {
                let result = ''

                if (title) {
                    result += `<i><strong>${title}</strong></i><br>`
                }

                result += descriptions.join('<br>')

                fullHTML += `<div>${result}</div>`
            })

            return fullHTML
        }
    }

    export default TransferService
</script>

<style lang="scss" scoped>
    .thumb {
        max-width: 350px;
    }

    .col-title {
        white-space: nowrap;
        max-width: fit-content;
    }

    .info-text {
        color: black;
        font-size: 1rem;
        font-style: italic;
    }

    .payment-status {
        width: 100%;
        text-align: center;
        display: flex;
        justify-content: center;
        min-width: fit-content;
        max-width: fit-content;
    }

    :deep(.v-dialog) {
        background: #fff;
        padding: 20px;
    }

    .modal-close-btn-block {
        position: relative;
        top: 12px;
        right: 12px;
    }
</style>
